<template>
  <iRow
    :class="['clickable', 'row-item', {selected: isEditing || currentlySelectedId == itemKey}]"
    :style-overrides="{paddingLeft: 'standard', paddingRight: 'extraSmall'}"
    gap="none"
    vertical-align="middle"
  >
    <!-- Inline editing mode for newly created items -->
    <iText
      v-if="isEditing"
      ref="nameInput"
      class="inline-name-input"
      contenteditable="true"
      data-placeholder="Enter name..."
      @keydown.enter.prevent="handleEnter"
      @keydown.esc.prevent="handleEscape"
      @blur="handleBlur"
    />

    <!-- Normal display mode -->
    <iText
      v-else
      width="fill"
      class="clickable"
      @click="$emit('select')"
    >
      <iRow gap="small" vertical-align="middle" wrap="nowrap">
        <iColumn
          class="item-name-container"
          gap="small"
          overflow="hidden"
          vertical-align="middle"
          width="fill"
          wrap="nowrap"
        >
          <iRow gap="small" vertical-align="middle" wrap="nowrap">
            <iIcon v-if="icon" :name="icon" />
            <iText
              :variant="isInvalid ? 'error' : 'branded'"
              :wrap="false"
              ellipsis
            >
              {{ label }}
            </iText>
            <span v-if="isInvalid" class="invalid-icon">
              <iIcon
                name="material-symbols:warning-outline-rounded"
                icon="material-symbols:warning-outline-rounded"
                size="small"
              />
            </span>
          </iRow>
        </iColumn>
        <iRow v-if="currentlySelectedId == itemKey" width="hug">
          <iButton
            size="extraSmall"
            variant="tertiary"
            icon-left="caret-right"
            icon-left-size="small"
            :style-overrides="{
              tertiaryVariantHoverBackgroundColor: '#e3e3e3',
              extraSmallPaddingTop: '2px',
              extraSmallPaddingBottom: '2px',
              extraSmallPaddingLeft: '2px',
              extraSmallPaddingRight: '2px',
            }"
          />
        </iRow>
        <iRow
          v-else
          class="actions"
          gap="none"
          vertical-align="middle"
          width="hug"
          wrap="nowrap"
        >
          <iButton
            :style-overrides="{
              tertiaryVariantHoverBackgroundColor: '#e3e3e3',
              extraSmallPaddingTop: '2px',
              extraSmallPaddingBottom: '2px',
              extraSmallPaddingLeft: '2px',
              extraSmallPaddingRight: '2px',
            }"
            icon-left="pencil"
            icon-left-size="small"
            size="extraSmall"
            variant="tertiary"
          />
        </iRow>
      </iRow>
    </iText>
  </iRow>
</template>

<script>
export default {
  name: "ListConfigRowItem",
  props: {
    itemKey: {
      type: [String, Number],
      required: true,
    },
    label: {
      type: String,
      required: true,
    },
    icon: {
      type: String,
      required: false,
      default: "",
    },
    currentlySelectedId: {
      type: [String, Number],
      required: false,
      default: 0,
    },
    isInvalid: {
      type: Boolean,
      required: false,
      default: false,
    },
    isEditing: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  emits: ["select", "rename"],
  data() {
    return {
      blurEnabled: false,
      rafId1: null,
      rafId2: null,
    };
  },
  watch: {
    isEditing(val) {
      if (val) {
        this._activateInput();
      } else {
        cancelAnimationFrame(this.rafId1);
        cancelAnimationFrame(this.rafId2);
        this.blurEnabled = false;
      }
    },
  },
  mounted() {
    if (this.isEditing) {
      this._activateInput();
    }
  },
  beforeUnmount() {
    cancelAnimationFrame(this.rafId1);
    cancelAnimationFrame(this.rafId2);
  },
  methods: {
    _activateInput() {
      this.blurEnabled = false;
      cancelAnimationFrame(this.rafId1);
      cancelAnimationFrame(this.rafId2);
      this.$nextTick(() => {
        this.rafId1 = requestAnimationFrame(() => {
          this.rafId2 = requestAnimationFrame(() => {
            const el = this.$refs.nameInput.$el;
            if (!el) {
              return;
            }
            el.focus();
            // Place cursor at end of any existing content
            const range = document.createRange();
            const sel = window.getSelection();
            range.selectNodeContents(el);
            range.collapse(false);
            sel.removeAllRanges();
            sel.addRange(range);
            this.blurEnabled = true;
          });
        });
      });
    },
    _getText(el) {
      return (el.textContent || "").trim();
    },
    handleEnter(e) {
      this.blurEnabled = false;
      this.$emit("rename", this._getText(e.target));
    },
    handleEscape() {
      this.blurEnabled = false;
      this.$emit("rename", "");
    },
    handleBlur(e) {
      if (!this.blurEnabled) {
        return;
      }
      this.$emit("rename", this._getText(e.target));
    },
  },
  styleGuide: () => ({
    rowItemBorderRadius: { "size.borderRadius": "standard" },
    rowItemPadding: { "size.spacing": "extraSmall" },
  }),
};
</script>

<style lang="scss" scoped>
.clickable {
  cursor: pointer;
}

.clickable:not(:hover) :deep(.actions) {
  display: none;
}

.row-item {
  border-radius: v-bind("$getStyles.rowItemBorderRadius");
  min-height: 29px;

  :deep(.text-link) {
    padding: v-bind("$getStyles.rowItemPadding");
  }

  &:hover {
    background-color: #b1cae3;

    :deep(.i-text) {
      color: #111827 !important;
    }

    :deep(.i-icon) {
      color: #111827;
      fill: #111827;
    }
  }
}

.selected {
  background-color: #8BB7E4;

  :deep(.i-text) {
    color: #111827;
  }

  :deep(.i-icon) {
    color: #111827;
    fill: #111827;
  }
}

.item-name-container {
  width: calc(100% - 0px) !important;

  .span.i-text {
    max-width: 100%;
  }
}

.invalid-label {
  :deep(.i-text) {
    color: #ef4444 !important;
  }
}

.invalid-icon {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;

  :deep(.i-icon) {
    color: #ef4444;
    fill: #ef4444;
  }
}

.inline-name-input {
  flex: 1;
  min-width: 20px;
  width: 100%;
  outline: 1px solid #9ca3af;
  border-radius: 4px;
  background: transparent;
  cursor: text;
  white-space: nowrap;
  overflow: hidden;

  &:focus {
    outline: none;
  }

  &:empty::before {
    content: attr(data-placeholder);
    color: #9ca3af;
    pointer-events: none;
  }
}
</style>
